import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Trophy, Euro, Clock, Phone, Mail, CalendarDays } from "lucide-react";

const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Filtri rapidi: oggi, ieri, ultimi 7/15/30 giorni + mesi da Agosto 2026. (fuso Europe/Rome)
function buildFilters() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const quick = [
    { key: "today", label: "Oggi", params: { date_from: isoDay(today), date_to: isoDay(today), days: 0 } },
    { key: "yesterday", label: "Ieri", params: { date_from: isoDay(yest), date_to: isoDay(yest), days: 0 } },
    { key: "d7", label: "Ultimi 7 giorni", params: { days: 7 } },
    { key: "d15", label: "Ultimi 15 giorni", params: { days: 15 } },
    { key: "d30", label: "Ultimi 30 giorni", params: { days: 30 } },
  ];
  const months = [];
  let y = 2026, m = 7; // Agosto 2026 (0-indexed 7)
  const curY = now.getFullYear(), curM = now.getMonth();
  const lastY = curY > y || (curY === y && curM >= m) ? curY : 2026;
  const lastM = curY > y || (curY === y && curM >= m) ? curM : 11; // fallback fino a Dicembre
  while (y < lastY || (y === lastY && m <= lastM)) {
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    months.push({
      key: `mon-${y}-${m}`,
      label: `${MONTH_NAMES[m]} ${y}`,
      params: { date_from: isoDay(first), date_to: isoDay(last), days: 0 },
    });
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return { quick, months: months.reverse() };
}

const eur = (v) => `€ ${Math.round(Number(v) || 0).toLocaleString("it-IT")}`;
const fmtDay = (iso) => (iso
  ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit", timeZone: "Europe/Rome" })
  : "—");
const fmtGap = (days) => {
  if (days == null) return "—";
  if (days < 1) return "in giornata";
  return `${days} g`;
};

export default function WonLeads() {
  const navigate = useNavigate();
  const { quick, months } = useMemo(buildFilters, []);
  const [filter, setFilter] = useState(quick[4]); // default: ultimi 30 giorni

  const { data, isLoading } = useQuery({
    queryKey: ["won-leads", filter.key],
    queryFn: async () => (await api.get("/analytics/won-leads", { params: filter.params })).data,
  });

  const stats = data?.stats;
  const items = data?.items || [];

  const FilterPill = ({ f }) => (
    <button
      data-testid={`won-filter-${f.key}`}
      onClick={() => setFilter(f)}
      className={`flex-shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
        filter.key === f.key
          ? "bg-emerald-600 text-white shadow-sm"
          : "border border-border bg-white text-muted-foreground hover:bg-secondary"
      }`}
    >{f.label}</button>
  );

  return (
    <div className="space-y-6" data-testid="won-leads-page">
      <div>
        <p className="text-sm text-muted-foreground">
          I lead <span className="font-semibold text-emerald-700">aggiudicati</span> nel periodo (per data di chiusura).
          Vedi prodotto, valore, tempo dalla richiesta alla vittoria e quante chiamate/email ha fatto il commerciale <b>prima</b> di chiudere.
        </p>
      </div>

      {/* Filtri */}
      <Card className="p-4 shadow-sm">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="won-quick-filters">
            {quick.map((f) => <FilterPill key={f.key} f={f} />)}
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="won-month-filters">
            <span className="mr-1 flex flex-shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <CalendarDays className="h-4 w-4" /> Mese
            </span>
            {months.map((f) => <FilterPill key={f.key} f={f} />)}
          </div>
        </div>
      </Card>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Kpi icon={Trophy} tone="emerald" label="Vendite" value={isLoading ? null : stats?.total_sales ?? 0} testid="kpi-sales" />
        <Kpi icon={Euro} tone="emerald" label="Fatturato" value={isLoading ? null : eur(stats?.total_revenue)} testid="kpi-revenue" />
        <Kpi icon={Clock} tone="amber" label="Tempo medio chiusura" value={isLoading ? null : fmtGap(stats?.avg_days_to_win)} testid="kpi-days" />
        <Kpi icon={Clock} tone="sky" label="Dal 1° contatto" value={isLoading ? null : fmtGap(stats?.avg_days_from_first_touch)} testid="kpi-touch" />
        <Kpi icon={Phone} tone="sky" label="Chiamate medie" value={isLoading ? null : (stats?.avg_calls ?? "—")} testid="kpi-calls" />
        <Kpi icon={Mail} tone="violet" label="Email medie" value={isLoading ? null : (stats?.emails_available ? (stats?.avg_emails ?? "—") : "n/d")} testid="kpi-emails" />
      </div>

      {/* Grafici */}
      {!isLoading && stats && stats.total_sales > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ChartCard title="Fatturato per mese" testid="chart-by-month">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.by_month} margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => eur(v)} />
                <Bar dataKey="revenue" name="Fatturato" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Tempo dalla richiesta alla vittoria" testid="chart-time">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.time_distribution} margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name="Vendite" fill="#d97706" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Prodotti più venduti" testid="chart-products">
            <ResponsiveContainer width="100%" height={Math.max(200, stats.by_product.length * 34)}>
              <BarChart data={stats.by_product.slice(0, 12)} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n, p) => [`${v} vendite · ${eur(p.payload.revenue)}`, "Vendite"]} />
                <Bar dataKey="sales" name="Vendite" fill="#7c3aed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Vendite per commerciale" testid="chart-commercials">
            <ResponsiveContainer width="100%" height={Math.max(200, stats.by_commercial.length * 44)}>
              <BarChart data={stats.by_commercial} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v, n, p) => [`${v} vendite · ${eur(p.payload.revenue)}`, "Vendite"]} />
                <Bar dataKey="sales" name="Vendite" radius={[0, 4, 4, 0]}>
                  {stats.by_commercial.map((_, i) => (
                    <Cell key={i} fill={["#0ea5e9", "#059669", "#f59e0b", "#7c3aed", "#ef4444"][i % 5]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Tabella */}
      <Card className="overflow-hidden shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <Trophy className="h-4 w-4 text-emerald-600" /> Lead vinti ({items.length})
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60">
                {["Nome", "Corso", "Prodotto", "Valore", "Richiesta", "Aggiudicato", "Tempo", "Dal 1° contatto", "Chiamate", "Email", "Commerciale"].map((h) => (
                  <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 11 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length ? (
                items.map((r, i) => (
                  <TableRow
                    key={i}
                    data-testid="won-lead-row"
                    className={r.lead_id ? "cursor-pointer hover:bg-secondary/50" : ""}
                    onClick={() => r.lead_id && navigate(`/leads/${r.lead_id}`)}
                  >
                    <TableCell className="text-sm font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm">{r.course_name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm">
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700" title={r.title}>{r.product}</span>
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-emerald-700 tabular-nums">{eur(r.value)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDay(r.received_at)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDay(r.won_at)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{fmtGap(r.days_to_win)}</TableCell>
                    <TableCell className="text-sm tabular-nums" data-testid="won-first-touch">{fmtGap(r.days_from_first_touch)}</TableCell>
                    <TableCell className="text-sm tabular-nums">
                      <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-sky-600" />{r.calls_before}<span className="text-xs text-muted-foreground">({r.calls_answered_before} risp.)</span></span>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {r.emails_before == null ? <span className="text-muted-foreground">n/d</span> : (
                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3 text-violet-600" />{r.emails_before}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.commercial || "—"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} className="py-16 text-center text-sm text-muted-foreground">
                    Nessun lead vinto in questo periodo.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

const TONES = {
  emerald: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  sky: "bg-sky-100 text-sky-700",
  violet: "bg-violet-100 text-violet-700",
};

function Kpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <Card className="p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${TONES[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          {value == null ? <Skeleton className="mt-1 h-6 w-16" /> : <p className="truncate text-lg font-bold">{value}</p>}
        </div>
      </div>
    </Card>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <Card className="shadow-sm" data-testid={testid}>
      <CardHeader><CardTitle className="font-display text-base tracking-tight">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
